// AiAgentSections.tsx · v12.10.25 (#2026 Language proofing: the language chip and the "Language proofing ›" link open the drawer; a Proofing column with lines / new / changed) · 2026-10-30
// AiAgentSections.tsx · v12.10.11.1 (#1465 Spanish proofreading 12.10.11) · 2026-10-09
// AiAgentSections.tsx · v12.10.11.1 (#1482 Logged-in platform user locked off) · 2026-10-09
// AiAgentSections.tsx · v12.10.11 (#1458 new text uses {organization.name} - the vocabulary token; {organization.practiceName} / {tenant.practiceName} stay readable as aliases) · 2026-10-08
// AiAgentSections · v12.10.10 (#1408 the message box declares its platform default - written text, never grey) · 2026-10-08 · VoiceSpectrum AI
// AiAgentSections · v12.10.8 (#1190): Identity extraction is GONE - its section, drawer and save. Vocabulary › Fields is the only
//   place fields are defined (Tushar 10/5, VOCABULARY-CLEANUP V5); its old look is kept in Documents\Old Screens.
// AiAgentSections.tsx — v12.10.5.1: Verify saves first (#1138 - silently when only that line changed, else "Save and continue?"); Cancel a test call (#1139); Verified = number + keys (#1140).
// AiAgentSections.tsx — v12.10.5 (UI-LOCK-12.10.5 v1.3): each Handoff line = number box (§38 mask, pill inside) + "After it answers, press" (#1132) + Verify with "Last verification" (#1131); a held After hours switch says why (#1128).
// AiAgentSections.tsx — v12.10.2 (#1114): Handoff gets an "After hours" group - After-hours line OR Outside operator line, each with its number, exactly one switched on (switching one on switches the other off); the After-hours line leaves the Live phone line card.
// AiAgentSections.tsx — v12.10.1 (#1086, UI-LOCK-12.10.1 v1.5 LOCKED): Handoff loses Whisper / Fallback / "all agents busy" (they live on the Transfer to staff and Route to operator cards now) and gains the Outside operator line (number + its own on/off) at the bottom, layout otherwise unchanged; Logged-in platform user, Users to ring, Handoff preference and Ring strategy are shown but cannot be switched on (Phase 3).
// AiAgentSections.tsx — v12.9.28 (#1065): Sample is a one-line try-out box (the default "Hi, I am {name}…" shown in gray, never saved) + play; Switch line is a play button with its sentence in the tooltip.
// AiAgentSections.tsx — v12.9.27 (#1044, UI-LOCK-12.9.27 LOCKED): Languages & Voices reads Language (chip 'English · EN') | Voice | Name | Sample (box: sentence + play, speaks the ROW's name) | Switch line (read-only box + play; edited on the Language detect card). Locale column removed; ? on every header.
// AiAgentSections.tsx — v12.9.26 (#951): Languages & Voices reads Language | Locale | Name | Voice | Sample | Switch line (+ its own play); the Voice box is narrower with the full name on hover.
// AiAgentSections.tsx — v12.9.24 (#912): Languages & Voices gains a Switch line column; Sample narrows to a play button with the sentence in its tooltip; rows stack under 600px.
// AiAgentSections.tsx — v12.2.20.0 (PRE-TOK2)
// AI Agent settings area (5 sections): real GET load + per-section Save/Discard wired to the
// AiAgentSettingsController endpoints, rich prototype-fidelity section UIs, server-side voice/text
// preview, and (v11.8.8.1, AAR12) per-section dirty state reported up to the Settings shell so the
// area/section red dots + "Save all changes" activate. See backlog AAR1..AAR16.
// v12.2.20.0 (PRE-TOK2): the Voicemail-greeting and Handoff-busy editors now show a live token-resolved
// v12.4.16 (#499): the Add Identifier drawer's markup now lives in components/vocabulary/VocabularyFieldDrawer
//                   and is rendered by BOTH this screen and Settings › Personalization › Vocabulary.
// PREVIEW box (the way the workflow drawer cards do) so {organization.practiceName} etc. render as the
// real tenant values on-screen as the page loads — not the literal token. Uses the shared resolveTokens
// util with a context (practiceName / phone / supported+primary language) computed server-side.
import { ReactNode, useEffect, useRef, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { HelpTooltip } from '../components/aiagent/HelpTooltip';
import { HandoffLineField, lineIsVerified, type LineCheck } from '../components/aiagent/HandoffLineField';   // v12.10.5
import { toE164, phoneError } from '../lib/phone';
import LanguageProofingDrawer, { type ProofingSummary } from '../components/aiagent/LanguageProofingDrawer';   // v12.10.25 (#2026)
import { useAccess } from '../lib/useAccess';   // v12.10.25 (#2026 Q3)
// v12.10.5 (#1122 / #1132): the first problem in the Handoff lines, or '' - shown on the disabled Save button.
function handoffProblem(h: { bizLine?: string | null; afterHoursLine?: string | null; operatorLine?: string | null; bizKeys?: string | null; afterHoursKeys?: string | null; operatorKeys?: string | null }): string {
  const lines: [string, string | null | undefined, string | null | undefined][] = [['Business-hours line', h.bizLine, h.bizKeys], ['After-hours line', h.afterHoursLine, h.afterHoursKeys], ['Operator line', h.operatorLine, h.operatorKeys]];
  for (const [label, n, k] of lines) {
    const e = phoneError(n); if (e) return `${label}: ${e}`;
    if (!/^[0-9*#,]*$/.test(k ?? '')) return `${label}: After it answers, press - only digits, * , # and commas`;
  }
  return '';
}
import { ApiError } from '../lib/api';
// v12.10.5: the server's own sentence for a refused request ({ error: "..." }), or ''.
const apiErrorText = (err: unknown): string => (err instanceof ApiError && err.body && typeof (err.body as { error?: unknown }).error === 'string') ? (err.body as { error: string }).error : '';
import { TokenPicker, TOKEN_GROUPS, type TokenGroup } from '../components/aiagent/TokenPicker';
// v12.7.3 (#649): the canonical catalog, so these three engine-spoken editors stop falling back to a static list.
import { PromptEditor, previewFailureText, useVocabulary, catalogGroups, langLabel } from '../components/PromptEditor';   // v12.9.22 (#886)
import { resolveTokens, type TokenContext } from '../lib/tokenPreview';
// v12.4.16 (#499): the Add Identifier drawer's markup MOVED into this shared component, which Settings ›
// Personalization › Vocabulary now renders too. One drawer, two entry points - Tushar: "we can call the same
// drawer from identity to here." Its FIELD_TYPES list is the catalog's (FieldTypes in VocabularyCatalog.cs),
// which supersedes IDENT_TYPES below.
import './settings-controls.css';
import './ai-agent.css';

// v12.9.22 (#844): `name` is the SPOKEN name for this language. Blank/absent means speak the one
// Agent name, which is also what every {agent.name} token falls back to.
export interface AiLang { code: string; voice: string; sortOrder: number; name?: string | null;
  // v12.9.24 (#912): the Switch line - this voice's first words when the call changes into its language with a new voice.
  switchLine?: string | null; switchLineDefault?: string | null }
export interface AiLangMessage { code: string; message: string | null }
export interface AiHandoff {
  liveOn: boolean; userOn: boolean; bizLine: string | null; afterHoursLine: string | null;
  preference: string; whisper: string | null; ringStrategy: string; ringTimeoutSec: number;
  fallback: string; users: number[]; busyMessages: AiLangMessage[];
  operatorLine?: string | null; operatorOn?: boolean;   // v12.10.1 (#1086): the Outside operator line
  afterHoursOn?: boolean;   // v12.10.2 (#1114): the After-hours line's switch - never on together with operatorOn
  // v12.10.5 (#1132): "After it answers, press" per line; (#1131): each line's Verify state - read only, never sent back.
  bizKeys?: string | null; afterHoursKeys?: string | null; operatorKeys?: string | null;
  checks?: Record<string, LineCheck>;
}
// v12.7.1 (#564): `strategy` and `onFail` are gone from the type, the wire and the database. 12.6.0 removed
// the only UI that set them; nothing has ever read them.
export interface AiIdentifier { name: string; key: string; type: string; isOn: boolean; sortOrder: number; entity?: string; field?: string; cleanup?: string }   // v12.2.43.0 (#235): Vocabulary link; v12.7.3 (#641): per-field input cleanup
export interface AiAgentData {
  agentEnabled: boolean; agentOffRoute: string; voicemailEnabled: boolean; handoffEnabled: boolean;
  agentName: string;
  languages: AiLang[];
  voicemailMessages: AiLangMessage[];
  handoff: AiHandoff;
  identifiers?: AiIdentifier[];   // v12.10.8 (#1190): no longer sent by the API
  phoneStatus: string; phoneNumber: string | null; phoneFacilities: { name: string; ok: boolean }[];
  platformUsers: { id: number; name: string; role: string }[];
  orgPrimaryPhone: string | null;
  // v12.2.20.0 (PRE-TOK2): token-preview context (computed server-side, mirrors the runtime resolver).
  practiceName?: string; supportedLanguages?: string; primaryLanguage?: string;
  provisionedLanguages: { code: string; voiceCode: string; voiceId: string; voiceName: string; providerCode: string; model: string }[];
  voiceCatalog: AiVoiceOption[];
}
export interface AiVoiceOption { language: string; code: string; name: string; voiceIdOrName: string; providerCode: string; model: string }

// v11.8.8.1 (AAR12): the section keys mirror AREA_SECTIONS['ai'] order in SettingsPage so the shell
// can light the matching nav red dot. The component reports this map + save/discard-all up.
export type AiSectionKey = 'phone' | 'details' | 'voicemail' | 'handoff';   // v12.10.8 (#1190): 'identity' removed
export type AiDirtyMap = Record<AiSectionKey, boolean>;
export interface AiAgentState { dirty: AiDirtyMap; anyDirty: boolean; saveAll: () => void; discardAll: () => void; }

// ── v12.6.0 (C1): PARSE_STRATEGIES and PARSE_FAILURE ARE GONE, BECAUSE NOTHING EVER READ THEM. ──────
//
// Tushar, 2026-09-21, setting the rule for this build: "if there are workarounds in code for this then
// that should be removed - code clean up, what you see on UI must be getting executed by engine."
//
// These two selects offered Fuzzy / Strict / Off and Re-prompt / Skip and handoff / Continue without
// it. `IdentityIdentifier` - the entity they are stored on - is referenced by exactly THREE files in
// the whole solution: this screen's controller, the DbContext that maps it, and the entity itself. The
// CallEngine has **zero** references. Every value a tenant has ever chosen here was written to the
// database and read by nobody; no live call has ever branched on one.
//
// The codebase already knew. VocabularyCatalog's own comment: "ident_type has shipped... since #235,
// written by T2 and stored - and read by nothing on a live call."
//
// WHAT ACTUALLY DOES THIS JOB, and has since 12.4.16/12.6.0: the field's DECLARED TYPE. The catalog
// says a field is zip or date or phone, and that one declaration drives the AI cleanup's description
// of it (#557), the deterministic tidy (#512), validation before store (#550) and canonicalisation
// (#528). "Strict format only" is what a declared type MEANS now, and it is enforced in the engine
// rather than described on a screen.
//
// v12.7.1 (#564): THE COLUMNS ARE NOW GONE TOO. This comment used to end "the COLUMNS stay for now" - the
// drop was waiting for a release that already carried a migration. It was pulled from 12.7.0 (an irreversible
// change touching the API contract and this type, in a release where everything else fixed something felt)
// and done properly here, as its own item: entity, IsRequired() mapping, the controller's normalise-on-write
// and round-trip-on-read, the seed's per-row OnFail, this interface, and the migration.
// ────────────────────────────────────────────────────────────────────────────────────────────────────

const QKEY = ['t2-ai-agent'];

export default function AiAgentSections({ onStateChange }: { onStateChange?: (s: AiAgentState) => void } = {}) {
  const queryClient = useQueryClient();
  // ── v12.7.3 (#649 / AC 649-e): THE THREE EDITORS THE ENGINE SPEAKS, ON THE CANONICAL CATALOG. ─────────
  // The Voicemail greeting, the Handoff busy message (both via LanguageMessageEditor) and the Handoff whisper
  // used TokenPicker with no `groups`, so they fell back to its static TOKEN_GROUPS - which cannot know this
  // tenant's own fields or Lists. #436/#437 fixed the DEFAULT's contents; it could never fix the fact that a
  // static default is not this tenant's vocabulary.
  const { catalog: vocabCatalog2, vocab: vocabLabels2, customFields: vocabCustom2, lists: vocabLists2 } = useVocabulary();
  const canonicalGroups = catalogGroups(vocabCatalog2, vocabLabels2, vocabCustom2, vocabLists2) ?? TOKEN_GROUPS;
  // v12.9.22 (#886): the known-token set for PromptEditor's unresolved-token note. The designer builds this
  // with availableTokens(doc, ...), but this page has no designer document - the canonical groups ARE the
  // catalogue here, so flattening them is the same set by a shorter road. Without it Check-PromptEditors
  // fails, and it is right to: a tenant typing {organisation.name} would get no warning.
  const canonicalKnown = useMemo(() => canonicalGroups.flatMap((g) => g.tokens), [canonicalGroups]);
  const { data, isLoading, error } = useQuery<AiAgentData>({
    queryKey: QKEY,
    queryFn: () => api.get<AiAgentData>('/t2/settings/ai-agent'),
  });

  const [saved, setSaved] = useState<AiAgentData | null>(null);
  const [draft, setDraft] = useState<AiAgentData | null>(null);
  // Lightweight inline toast for voice/text preview feedback (AAR13). Auto-dismisses.
  const [toast, setToast] = useState<string>('');
  const toastTimer = useRef<number | undefined>(undefined);
  function showToast(msg: string) {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(''), 3200);
  }
  // local-only: tracks whether voicemail was auto-enabled as the off-route safety net (so we can undo it).
  const [vmAuto, setVmAuto] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);
  // v12.10.25 (#2026): Language proofing - the drawer, its counts per language, and who may press Done (Q3: AI Agent › Agent details edit)
  const [proofLang, setProofLang] = useState<string | null>(null);
  const access = useAccess();
  const canProof = access.hasAccess('s:AI Agent:Agent details', 'edit');
  const proofing = useQuery<ProofingSummary>({ queryKey: ['t2-language-proofing'], queryFn: () => api.get<ProofingSummary>('/t2/language-proofing'), staleTime: 30_000, retry: false });
  const [pickerSel, setPickerSel] = useState<string[]>([]);

  useEffect(() => {
    if (!data) return;
    setSaved(structuredClone(data));
    setDraft(structuredClone(data));
  }, [data]);

  function onSaved(fresh: AiAgentData) {
    setSaved(structuredClone(fresh));
    setDraft(structuredClone(fresh));
    queryClient.setQueryData(QKEY, fresh);
  }

  const savePhone = useMutation({
    mutationFn: (d: AiAgentData) => api.put<AiAgentData>('/t2/settings/ai-agent/phone-activation', { agentEnabled: d.agentEnabled, agentOffRoute: d.agentOffRoute, voicemailEnabled: d.voicemailEnabled }),
    onSuccess: onSaved,
  });
  const saveDetails = useMutation({
    // v12.9.27 (#1044): the switch line is no longer sent from here - the Language detect card edits it (PUT switch-line).
    mutationFn: (d: AiAgentData) => api.put<AiAgentData>('/t2/settings/ai-agent/agent-details', { agentName: d.agentName, languages: d.languages.map(({ switchLine: _sl, switchLineDefault: _sd, ...rest }) => rest) }),
    onSuccess: onSaved,
  });
  const saveVoicemail = useMutation({
    mutationFn: (d: AiAgentData) => api.put<AiAgentData>('/t2/settings/ai-agent/voicemail', { enabled: d.voicemailEnabled, messages: d.voicemailMessages }),
    onSuccess: onSaved,
  });
  const saveHandoff = useMutation({
    // v12.10.5: checks are read only - never sent back.
    mutationFn: (d: AiAgentData) => { const { checks: _c, ...h } = d.handoff; return api.put<AiAgentData>('/t2/settings/ai-agent/handoff', { enabled: d.handoffEnabled, ...h }); },
    onSuccess: onSaved,
    onError: (err: unknown) => { const t = apiErrorText(err); if (t) showToast(t); },   // v12.10.5 (#1122): "Only US and Canada numbers for now"
  });
  // v12.10.5 (#1131): Verify a line. Only the Verify state is taken from the answer - an unsaved edit elsewhere stays.
  const mergeChecks = (fresh: AiAgentData) => {
    const c = fresh.handoff.checks;
    setSaved((x) => (x ? { ...x, handoff: { ...x.handoff, checks: c } } : x));
    setDraft((x) => (x ? { ...x, handoff: { ...x.handoff, checks: c } } : x));
  };
  const [verifyErr, setVerifyErr] = useState<Record<string, string>>({});
  const verifyLine = useMutation({
    mutationFn: (line: string) => api.post<AiAgentData>('/t2/settings/ai-agent/handoff/verify', { line }),
    onMutate: (line: string) => setVerifyErr((e) => ({ ...e, [line]: '' })),
    onSuccess: mergeChecks,
    onError: (err: unknown, line: string) => setVerifyErr((e) => ({ ...e, [line]: apiErrorText(err) || 'The test call could not be placed.' })),
  });
  // v12.10.5.1 (#1139): Cancel hangs the test call up and leaves no trace.
  const cancelVerify = useMutation({
    mutationFn: (line: string) => api.post<AiAgentData>('/t2/settings/ai-agent/handoff/verify/cancel', { line }),
    onSuccess: mergeChecks,
  });
  // v12.10.5.1 (#1138, Tushar 10/3): Verify with unsaved changes. Only this line's number / keys changed -> save silently
  // and call. Anything else in the section changed too -> "There are unsaved changes in this section. Save and continue?"
  const LINE_FIELDS: Record<string, (keyof AiHandoff)[]> = {
    biz: ['bizLine', 'bizKeys'], afterHours: ['afterHoursLine', 'afterHoursKeys'], operator: ['operatorLine', 'operatorKeys'],
  };
  async function startVerify(line: string) {
    if (!draft || !saved) return;
    setVerifyErr((e) => ({ ...e, [line]: '' }));
    const strip = (h: AiHandoff, all: boolean) => {
      const c: Record<string, unknown> = { ...h };
      delete c.checks;
      if (!all) (LINE_FIELDS[line] ?? []).forEach((f) => { delete c[f as string]; });
      return JSON.stringify(c);
    };
    const anyUnsaved = strip(draft.handoff, true) !== strip(saved.handoff, true) || draft.handoffEnabled !== saved.handoffEnabled;
    if (anyUnsaved) {
      const onlyThisLine = strip(draft.handoff, false) === strip(saved.handoff, false) && draft.handoffEnabled === saved.handoffEnabled;
      if (!onlyThisLine && !window.confirm('There are unsaved changes in this section. Save and continue?')) return;
      const problem = handoffProblem(draft.handoff);
      if (problem) { setVerifyErr((e) => ({ ...e, [line]: problem })); return; }
      try { await saveHandoff.mutateAsync(draft); }
      catch (err) { setVerifyErr((e) => ({ ...e, [line]: apiErrorText(err) || 'Could not save - the test call was not placed.' })); return; }
    }
    verifyLine.mutate(line);
  }
  const anyCalling = !!draft?.handoff.checks && Object.values(draft.handoff.checks).some((c) => c?.calling);
  useEffect(() => {
    if (!anyCalling) return;
    const t = window.setInterval(() => { api.get<AiAgentData>('/t2/settings/ai-agent').then(mergeChecks).catch(() => {}); }, 2500);
    return () => window.clearInterval(t);
  }, [anyCalling]); // eslint-disable-line react-hooks/exhaustive-deps
  // AAR12: report per-section dirty state + save/discard-all up to the Settings shell so the area
  // nav red dot, the per-section sub dots, and the page "Save all changes" button activate.
  useEffect(() => {
    if (!draft || !saved || !onStateChange) return;
    const eqx = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const dirty: AiDirtyMap = {
      phone: !eqx([draft.agentEnabled, draft.agentOffRoute, draft.voicemailEnabled], [saved.agentEnabled, saved.agentOffRoute, saved.voicemailEnabled]),
      details: !eqx([draft.agentName, draft.languages], [saved.agentName, saved.languages]),
      voicemail: !eqx([draft.voicemailEnabled, draft.voicemailMessages], [saved.voicemailEnabled, saved.voicemailMessages]),
      handoff: !eqx([draft.handoffEnabled, draft.handoff], [saved.handoffEnabled, saved.handoff]),
    };
    onStateChange({
      dirty,
      anyDirty: Object.values(dirty).some(Boolean),
      // Save every dirty section sequentially from this one draft snapshot, so the per-section
      // endpoints don't race each other's full-state responses (each onSaved resets draft).
      saveAll: async () => {
        try {
          if (dirty.phone) await savePhone.mutateAsync(draft);
          if (dirty.details) await saveDetails.mutateAsync(draft);
          if (dirty.voicemail) await saveVoicemail.mutateAsync(draft);
          if (dirty.handoff && !handoffProblem(draft.handoff)) await saveHandoff.mutateAsync(draft);   // v12.10.5: never send an invalid line
        } catch { /* mutation error state surfaces the failure */ }
      },
      discardAll: () => { setDraft(saved ? structuredClone(saved) : draft); setVmAuto(false); },
    });
  }, [draft, saved, onStateChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // v12.9.28 (#1065): what the tenant typed in a row's Sample box to TRY the voice. Page state only - never in the draft,
  // never saved; cleared when the page reloads. Declared ABOVE the loading return - a hook after it would break the hook order.
  const [tryOut, setTryOut] = useState<Record<string, string>>({});
  if (isLoading || !draft || !saved) return <div className="settings-loading">Loading AI Agent settings…</div>;
  if (error) return <div className="settings-error">AI Agent settings could not be loaded.</div>;

  const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const patch = (p: Partial<AiAgentData>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchHandoff = (p: Partial<AiHandoff>) => setDraft((d) => (d ? { ...d, handoff: { ...d.handoff, ...p } } : d));
  // v12.10.5 (#1128): why an After hours switch is held - no number yet, or the line is not verified.
  const heldWhy = (n: string | null | undefined, k: string | null | undefined, c: LineCheck | undefined) =>
    !toE164(n) ? 'To enable, type the phone number' : !lineIsVerified(n, k, c) ? 'Verify this line before calls can reach it' : '';
  const ahWhy = heldWhy(draft.handoff.afterHoursLine, draft.handoff.afterHoursKeys, draft.handoff.checks?.afterHours);
  const opWhy = heldWhy(draft.handoff.operatorLine, draft.handoff.operatorKeys, draft.handoff.checks?.operator);
  // v12.10.5 (#1122 / #1132): Save stays off while a line or its keys are invalid (each says why in place).
  const handoffInvalid = handoffProblem(draft.handoff);
  const reset = (keys: (keyof AiAgentData)[]) => setDraft((d) => {
    if (!d) return d;
    const next = { ...d };
    keys.forEach((k) => { (next as unknown as Record<string, unknown>)[k] = structuredClone((saved as unknown as Record<string, unknown>)[k]); });
    return next;
  });

  // Phone & Activation: master toggle + off-route, with the prototype's "calls are never dropped"
  // safety net (auto-enable voicemail when the agent goes off and routes to it). voicemailEnabled is
  // persisted by the phone-activation PUT, so the net survives a save.
  function toggleAgent() {
    if (!draft) return;
    const on = !draft.agentEnabled;
    const p: Partial<AiAgentData> = { agentEnabled: on };
    if (!on) {
      let route = draft.agentOffRoute;
      let vm = draft.voicemailEnabled;
      if (!draft.handoffEnabled && route === 'handoff') route = 'voicemail';
      if (route === 'voicemail' && !vm) { vm = true; setVmAuto(true); }
      if (!draft.handoffEnabled && !vm) { vm = true; route = 'voicemail'; setVmAuto(true); }
      p.agentOffRoute = route; p.voicemailEnabled = vm;
    } else if (vmAuto) {
      p.voicemailEnabled = false; setVmAuto(false);
    }
    patch(p);
  }
  function setOffRoute(v: string) {
    if (!draft) return;
    if (v === 'handoff' && !draft.handoffEnabled) return;
    const p: Partial<AiAgentData> = { agentOffRoute: v };
    if (v === 'voicemail' && !draft.voicemailEnabled) { p.voicemailEnabled = true; setVmAuto(true); }
    if (v === 'handoff' && vmAuto) { p.voicemailEnabled = false; setVmAuto(false); }
    patch(p);
  }

  // Agent Details: voice picks + the add-language modal (provisioned languages only).
  function setVoice(i: number, code: string) {
    setDraft((d) => d ? { ...d, languages: d.languages.map((l, j) => (j === i ? { ...l, voice: code } : l)) } : d);
  }
  // v12.9.22 (#844): the spoken name for one language. Stored as typed; the API drops blanks so that
  // absence stays the fallback rather than an empty string overriding the agent name.
  function setLangName(i: number, name: string) {
    setDraft((d) => d ? { ...d, languages: d.languages.map((l, j) => (j === i ? { ...l, name } : l)) } : d);
  }
  // v12.9.27 (#1044): setSwitchLine retired - the Switch line is shown here read-only and edited on the Language detect card.
  // Shared server-side preview: resolves the registry voice → synthesizes MP3 → plays it, with
  // user-visible toast feedback at every step (AAR13). Provider keys never leave the server.
  async function runPreview(language: string, voiceCode: string, sample: string, label: string) {
    if (!voiceCode) { showToast('Select a voice first.'); return; }
    showToast(`Synthesizing ${label}…`);
    try {
      const res = await api.post<{ ok?: boolean; reason?: string | null; reference?: string | null; audioUrl: string | null }>(
        '/t2/settings/ai-agent/voice-preview', { language, voiceId: voiceCode, sample });
      if (res.audioUrl) {
        showToast(`▶ Playing ${label}`);
        try { await new Audio(res.audioUrl).play(); }
        catch { showToast('Tap anywhere, then Preview again — the browser blocked autoplay.'); }
      } else {
        // v12.8.2 (#678): generic wording + a reference; the provider's own error never reaches this toast.
        showToast(previewFailureText(res));
      }
    } catch {
      showToast('Preview failed — the voice service did not respond.');
    }
  }
  // v12.9.27 (#1044, his report): "when i preview voice it still say, hi I am victoria" - the sample speaks the ROW's
  // own name ("Hi, I am Skylar"); the Agent name only when the row's Name is blank. The box shows the same sentence.
  function previewVoice(language: string, voiceCode: string, rowName?: string | null) {
    runPreview(language, voiceCode, sampleGreeting(language, (rowName ?? '').trim() || (draft?.agentName ?? '')), `${langName(language)} voice`);
  }
  // Preview a per-language message (voicemail greeting / busy message) or the whisper, using that
  // language's selected voice and the typed text (falling back to the prototype default). (AAR5)
  function previewMessage(code: string, text: string, fallback: string, label: string) {
    if (!draft) return;
    const opts = voicesFor(draft.voiceCatalog, code);
    const lang = draft.languages.find((l) => l.code === code);
    const voice = lang ? resolveVoiceCode(lang.voice, opts) : (opts[0]?.code ?? '');
    runPreview(code, voice, (text && text.trim()) || fallback, label);
  }
  function openLangPicker() { setPickerSel(draft ? draft.languages.map((l) => l.code) : []); setLangModalOpen(true); }
  function togglePick(code: string) { setPickerSel((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code])); }
  function saveLangs() {
    if (!draft) return;
    const next: AiLang[] = draft.provisionedLanguages
      .filter((p) => pickerSel.includes(p.code))
      .map((p, idx) => {
        const existing = draft.languages.find((l) => l.code === p.code);
        return { code: p.code, voice: existing ? existing.voice : p.voiceCode, sortOrder: idx };
      });
    patch({ languages: next });
    setLangModalOpen(false);
  }

  // Per-language message editors (Voicemail greeting + Handoff busy message).
  function onVmMessage(code: string, value: string) {
    setDraft((d) => {
      if (!d) return d;
      const has = d.voicemailMessages.some((m) => m.code === code);
      const next = has ? d.voicemailMessages.map((m) => (m.code === code ? { ...m, message: value } : m)) : [...d.voicemailMessages, { code, message: value }];
      return { ...d, voicemailMessages: next };
    });
  }
  function onBusyMessage(code: string, value: string) {
    setDraft((d) => {
      if (!d) return d;
      const arr = d.handoff.busyMessages;
      const has = arr.some((m) => m.code === code);
      const next = has ? arr.map((m) => (m.code === code ? { ...m, message: value } : m)) : [...arr, { code, message: value }];
      return { ...d, handoff: { ...d.handoff, busyMessages: next } };
    });
  }
  // Handoff card toggles + user ring list.
  function toggleCard(which: 'live' | 'user') {
    setDraft((d) => {
      if (!d) return d;
      const h = { ...d.handoff };
      if (which === 'live') h.liveOn = !h.liveOn; else h.userOn = !h.userOn;
      if (h.liveOn && !h.userOn) h.preference = 'live';
      if (h.userOn && !h.liveOn) h.preference = 'user';
      return { ...d, handoff: h };
    });
  }
  function toggleHandoffUser(id: number) {
    setDraft((d) => {
      if (!d) return d;
      const users = d.handoff.users.includes(id) ? d.handoff.users.filter((u) => u !== id) : [...d.handoff.users, id];
      return { ...d, handoff: { ...d.handoff, users } };
    });
  }
  const phoneDirty = !eq([draft.agentEnabled, draft.agentOffRoute, draft.voicemailEnabled], [saved.agentEnabled, saved.agentOffRoute, saved.voicemailEnabled]);
  const detailsDirty = !eq([draft.agentName, draft.languages], [saved.agentName, saved.languages]);
  const vmDirty = !eq([draft.voicemailEnabled, draft.voicemailMessages], [saved.voicemailEnabled, saved.voicemailMessages]);
  const handoffDirty = !eq([draft.handoffEnabled, draft.handoff], [saved.handoffEnabled, saved.handoff]);

  // v12.2.20.0 (PRE-TOK2): token-preview context — resolves {organization.*} in the message previews
  // the same way the runtime does. Uses saved (server) values so the preview reflects the live call.
  const tokenCtx: TokenContext = {
    practiceName: saved.practiceName ?? '',
    phone: saved.orgPrimaryPhone ?? '',
    supportedLanguages: saved.supportedLanguages ?? '',
    primaryLanguage: saved.primaryLanguage ?? '',
  };

  return (
    <div className="organization-panel ai-agent-panel">
      <Section title="Phone & Activation" description="Your phone number and whether the AI Agent is answering."
        dirty={phoneDirty} saving={savePhone.isPending}
        onSave={() => savePhone.mutate(draft)} onDiscard={() => reset(['agentEnabled', 'agentOffRoute', 'voicemailEnabled'])}>
        <div className="ai-agent-body">
          <PhoneCard status={draft.phoneStatus} number={draft.phoneNumber} facilities={draft.phoneFacilities} />
          <div className={`enable-row${draft.agentEnabled ? '' : ' off'}`}>
            <div className="enable-text">
              <b>AI Agent enabled <span className="aa-req">*</span></b>
              <HelpTooltip sections={[
                ['What it does', 'Master switch for the AI receptionist on inbound calls.'],
                ['When it fires', 'If off, all inbound calls route directly to the fallback handoff line or voicemail without any AI interaction.'],
                ['Example', 'Turn off during a system outage or compliance freeze — callers go straight to your front desk line.'],
              ]} />
              <span className="enable-sub">When enabled, the agent answers on this number 24/7.</span>
            </div>
            <button type="button" className={`day-toggle${draft.agentEnabled ? '' : ' off'}`} onClick={toggleAgent} aria-label="Toggle AI Agent"><span className="knob" /></button>
          </div>
          {!draft.agentEnabled && (
            <div className="off-route">
              <div className="or-top">
                <div className="or-label">When the AI Agent is off
                  <HelpTooltip sections={[
                    ['What it does', 'Sets where inbound calls go while the AI Agent is switched off — so there’s always a defined path and calls are never dropped.'],
                    ['When it fires', 'Applies to every inbound call while the AI Agent toggle above is off.'],
                    ['Example', <><em>Route to handoff</em> → calls ring your phone line / platform users. <em>Route to voicemail</em> → callers go straight to the voicemail greeting.</>],
                  ]} />
                  <span className="aa-hint">— so calls are never dropped</span>
                </div>
                <div className="seg-group">
                  <button type="button" className={`seg${draft.agentOffRoute === 'handoff' ? ' on' : ''}`} onClick={() => setOffRoute('handoff')} disabled={!draft.handoffEnabled} title={!draft.handoffEnabled ? 'Enable Handoff to use this' : undefined}><span className="seg-dot" />Route to handoff</button>
                  <button type="button" className={`seg${draft.agentOffRoute === 'voicemail' ? ' on' : ''}`} onClick={() => setOffRoute('voicemail')}><span className="seg-dot" />Route to voicemail</button>
                </div>
              </div>
              {!draft.handoffEnabled && <div className="or-note">Handoff is turned off — enable it in the Handoff section to route there.</div>}
              {vmAuto && <div className="or-note">Voicemail was turned on automatically so callers always have somewhere to go. You can customize it in the Voicemail section.</div>}
            </div>
          )}
        </div>
      </Section>

      <Section title="Agent Details" description="The agent's name and the languages & voices it uses."
        dirty={detailsDirty} saving={saveDetails.isPending}
        onSave={() => saveDetails.mutate(draft)} onDiscard={() => reset(['agentName', 'languages'])}>
        <div className="ai-agent-body">
          <div className="aa-fr">
            <div className="aa-f">
              <label>Agent name <span className="aa-req">*</span>
                <HelpTooltip sections={[
                  ['What it does', 'The name the AI uses to introduce itself — but only where the greeting references it. If your greeting has no agent-name token, this value isn’t spoken anywhere.'],
                  ['When it fires', 'Spoken wherever the greeting (or a re-prompt) inserts the agent name. The greeting itself is configured in Workflow.'],
                  ['Example', <>Greeting &ldquo;Hi, I am {'{agent name}'}&hellip;&rdquo; &rarr; caller hears &ldquo;Hi, I am <em>{draft.agentName || 'Victoria'}</em>.&rdquo;</>],
                ]} />
                <span className="aa-hint">— how the agent introduces itself</span>
              </label>
              <input value={draft.agentName} onChange={(e) => patch({ agentName: e.target.value })} placeholder="your assistant" />
            </div>
            <div className="aa-f" />
          </div>

          <h4 className="aa-subhead">Languages &amp; voices</h4>
          <p className="aa-sub-note">Voices below are the catalog your plan licenses, filtered to each language.</p>
          <div className="lv-table-wrap">
            <table className="lv-table">
              {/* v12.9.22 (#844) - layout confirmed by him 2026-09-28: Language | Locale | Name | Voice | Sample,
                    with Greeting-sample and Actions collapsed into ONE control, the same shape as T4's PREVIEW TEXT. */}
              {/* v12.9.24 (#912) - HIS PLACEMENT 2026-09-29: a Switch line column beside the voice it belongs to;
                    Sample narrows to a play button with the sentence in its tooltip; rows stack on a phone. */}
              {/* v12.9.26 (#951, his words: "Preview should go after agent selection. Voice box can be reduced. play
                    button next to switch line is also good."): Sample moves right after Voice; Switch line gets its own play. */}
              {/* v12.9.27 (#1044, LOCKED 2026-10-01 - UI-LOCK-12.9.27): Language · Voice · Name · Sample · Switch line. The
                    Locale column is gone (a fixed display list in this page, not from the voice agent, not used by calls).
                    Every header carries its ?. Sample and Switch line are boxes showing the sentence with its play button. */}
              <thead><tr>
                <th>Language{' '}<HelpTooltip sections={[
                  ['What it is', 'A language your agent speaks on calls, in the platform’s format — its name and two-letter code.'],
                  ['Order', 'The first row is your primary language. Add or remove languages with + Add language.'],
                ]} /></th>
                <th>Voice{' '}<HelpTooltip sections={[
                  ['What it does', 'The voice that speaks this language on every call.'],
                  ['Choices', 'The voices your plan licenses for this language. Play the Sample to hear it.'],
                ]} /></th>
                <th>Name{' '}<HelpTooltip sections={[
                  ['What it does', 'The name this voice uses when it introduces itself in this language.'],
                  ['Empty', 'Leave empty to use the Agent name above.'],
                  ['Example', 'English → Skylar, Spanish → Valentina.'],
                ]} /></th>
                <th>Sample{' '}<HelpTooltip sections={[
                  ['What it does', 'Type any sentence and play it in this row’s voice. Empty, it plays a short greeting with this row’s name. What you type here is never saved.'],
                  ['Example', '“Hi, I am Skylar. How can I help you today?”'],
                ]} /></th>
                <th>Switch line{' '}<HelpTooltip sections={[
                  ['What it does', 'The first words this voice says when a call changes into this language AND the caller hears a different voice as a result. No name, no “Sure” - one short line.'],
                  ['When it fires', 'Only when the voice actually changes. One multilingual voice serving both languages - no line, because nothing changed for the caller.'],
                  ['Where to edit', 'Workflow › Language detect card › Switch line, on this language’s tab. Hover the play button to read it; press it to hear it in this voice.'],
                  ['Empty', 'The platform line is used.'],
                ]} /></th>
                <th>Proofing{' '}<HelpTooltip sections={[
                  ['What it does', 'Every line the agent says in this language - the workflow cards and the phrases. New = never proofread; Changed = the English or the line changed since.'],
                  ['Open it', 'Click the language chip, or Language proofing › below the table.'],
                ]} /></th>
              </tr></thead>
              <tbody>
                {draft.languages.map((l, i) => {
                  const opts = voicesFor(draft.voiceCatalog, l.code);
                  const sel = resolveVoiceCode(l.voice, opts);
                  const known = opts.some((o) => o.code === sel);
                  const sample = sampleGreeting(l.code, (l.name ?? '').trim() || draft.agentName);
                  const sw = (l.switchLine ?? '').trim() || (l.switchLineDefault ?? '').trim();
                  return (
                    <tr key={`${l.code}-${i}`}>
                      <td className="lv-lang">
                        {/* v12.10.25 (#2026): the chip opens Language proofing for this language; the dot = lines to proofread */}
                        <span className="lv-langchip lp-open" role="button" tabIndex={0} title="Open Language proofing"
                              onClick={() => setProofLang(l.code.toLowerCase())} onKeyDown={(e) => { if (e.key === 'Enter') setProofLang(l.code.toLowerCase()); }}>
                          {(() => { const p = proofing.data?.languages.find((x) => x.code === l.code.toLowerCase()); return p && (p.new + p.changed) > 0 ? <span className="lp-dot" /> : null; })()}
                          {langLabel({ code: l.code, displayName: langName(l.code) })}
                        </span>
                      </td>
                      <td>
                        <select className="lv-voice" value={sel} onChange={(e) => setVoice(i, e.target.value)}
                                title={opts.find((o) => o.code === sel)?.name ?? sel}>
                          {!known && sel && <option value={sel}>{sel}</option>}
                          {opts.map((o) => <option key={o.code} value={o.code} title={o.name}>{o.name}</option>)}
                          {opts.length === 0 && <option value="">No voices available</option>}
                        </select>
                      </td>
                      {/* v12.9.22 (#844): blank speaks the Agent name above - the placeholder shows what that would be. */}
                      <td>
                        <input
                          className="lv-name"
                          value={l.name ?? ''}
                          placeholder={draft.agentName || 'Agent name'}
                          aria-label={`Spoken name in ${langName(l.code)}`}
                          onChange={(e) => setLangName(i, e.target.value)}
                        />
                      </td>
                      <td data-label="Sample">
                        {/* v12.9.28 (#1065): a one-line try-out box - type anything and play it in this voice. */}
                        <span className="lv-says lv-try">
                          <button type="button" className="lv-says-play"
                                  onClick={() => runPreview(l.code, sel, (tryOut[l.code] ?? '').trim() || sample, `${langName(l.code)} voice`)}
                                  aria-label={`Play the ${langName(l.code)} sample`} title="Play in this voice">&#9654;</button>
                          <input className="lv-try-input" value={tryOut[l.code] ?? ''} placeholder={sample}
                                 aria-label={`Try a sentence in the ${langName(l.code)} voice`}
                                 title="Type a sentence to hear it in this voice - not saved"
                                 onChange={(e) => { const v = e.target.value; setTryOut((m) => ({ ...m, [l.code]: v })); }}
                                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runPreview(l.code, sel, (tryOut[l.code] ?? '').trim() || sample, `${langName(l.code)} voice`); } }} />
                        </span>
                      </td>
                      <td data-label="Switch line">
                        {/* v12.9.28 (#1065): play only - the sentence is in the tooltip; edited on the Language detect card. */}
                        <button type="button" className="lv-says-play lv-switch-play" disabled={!sw}
                                onClick={() => runPreview(l.code, sel, sw, `${langName(l.code)} switch line`)}
                                aria-label={`Play the ${langName(l.code)} switch line`}
                                title={sw ? `“${sw}” - edited on Workflow › Language detect card` : 'No switch line'}>&#9654;</button>
                      </td>
                      <td data-label="Proofing" className="lv-proof">
                        {(() => {
                          const p = proofing.data?.languages.find((x) => x.code === l.code.toLowerCase());
                          if (!p) return proofing.isLoading ? '…' : '-';
                          const isPrimary = proofing.data?.primary === p.code;
                          return <>{p.lines} lines{p.new ? <> · <b>{p.new} new</b></> : null}{p.changed ? ` · ${p.changed} changed` : ''}{isPrimary && !p.new && !p.changed ? ' · the source' : ''}</>;
                        })()}
                      </td>
                    </tr>
                  );
                })}
                {draft.languages.length === 0 && <tr><td colSpan={6} className="lv-greet">No languages selected yet — add one below.</td></tr>}
              </tbody>
            </table>
          </div>
          {/* v12.10.25 (#2026): both entry points - the chip above and this link, always next to + Add language */}
          <div className="lp-link-row">
            <button type="button" className="lv-add" style={{ marginTop: 0 }} onClick={openLangPicker}>+ Add language</button>
            <button type="button" className="lp-link" onClick={() => setProofLang((proofing.data?.languages.find((x) => x.code !== proofing.data?.primary && (x.new + x.changed) > 0) ?? proofing.data?.languages[0])?.code ?? (draft.languages[0]?.code ?? 'en').toLowerCase())}>Language proofing ›</button>
            {(() => {
              const ls = (proofing.data?.languages ?? []).filter((x) => x.new + x.changed > 0);
              const total = ls.reduce((a, x) => a + x.new + x.changed, 0);
              return total ? <span className="aa-sub-note" style={{ margin: 0 }}>{total} lines new or changed ({ls.map((x) => `${x.code.toUpperCase()} ${x.new + x.changed}`).join(' · ')})</span> : null;
            })()}
          </div>
          <LanguageProofingDrawer open={!!proofLang} initialLang={proofLang ?? 'en'} canEdit={canProof}
            voices={Object.fromEntries(draft.languages.map((l) => [l.code.toLowerCase(), resolveVoiceCode(l.voice, voicesFor(draft.voiceCatalog, l.code))]))}
            onClose={() => setProofLang(null)} onSaved={() => { proofing.refetch(); queryClient.invalidateQueries({ queryKey: QKEY }); }} />
          <p className="aa-foot-note">Languages available here are the ones provisioned for your account.</p>

          {langModalOpen && (
            <div className="aa-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setLangModalOpen(false); }}>
              <div className="aa-modal lang-modal">
                <button className="aa-modal-x" type="button" onClick={() => setLangModalOpen(false)} aria-label="Close">&#10005;</button>
                <h4>Add languages</h4>
                <p>Choose the languages your agent speaks. These are the languages provisioned for your account.</p>
                <div className="lang-cards">
                  {draft.provisionedLanguages.map((p) => (
                    <button key={p.code} type="button" className={`lang-card${pickerSel.includes(p.code) ? ' on' : ''}`} onClick={() => togglePick(p.code)}>{langName(p.code)} &middot; {codeBadge(p.code)}</button>
                  ))}
                  {draft.provisionedLanguages.length === 0 && <p className="aa-sub-note">No provisioned languages found for this organization.</p>}
                </div>
                <div className="aa-mact"><button type="button" className="aa-mc" onClick={() => setLangModalOpen(false)}>Cancel</button><button type="button" className="aa-ma" onClick={saveLangs}>Save languages</button></div>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title="Voicemail" description="When enabled, unanswered calls can leave a message."
        dirty={vmDirty} saving={saveVoicemail.isPending}
        onSave={() => saveVoicemail.mutate(draft)} onDiscard={() => reset(['voicemailEnabled', 'voicemailMessages'])}
        toggle={{ on: draft.voicemailEnabled, onToggle: (v) => patch({ voicemailEnabled: v }) }}>
        <div className="ai-agent-body">
          <h4 className="aa-subhead">Voicemail greeting<HelpTooltip sections={[
            ['What it does', 'The message callers hear before leaving a voicemail. Written per language so each caller hears it in their own.'],
            ['When it fires', 'Whenever a call routes to voicemail — after-hours, no answer on handoff, or when a fallback sends it there.'],
            ['Example', <>&ldquo;You&rsquo;ve reached the voicemail for <em>your clinic</em>. Please leave a message after the tone&hellip;&rdquo;</>],
          ]} /></h4>
          <p className="aa-sub-note">Played when a call routes to voicemail. Switch language to edit each version.</p>
          <LanguageMessageEditor groups={canonicalGroups} known={canonicalKnown} langs={draft.languages} messages={draft.voicemailMessages} onChange={onVmMessage} disabled={!draft.voicemailEnabled}
            placeholderFor={vmDefault} tokenCtx={tokenCtx} onPreview={(code, text) => previewMessage(code, text, vmDefault(code), `${langName(code)} voicemail`)} />
        </div>
      </Section>

      <Section title="Handoff" description="Transfer a live caller to a human — by phone line, a logged-in platform user, or both."
        dirty={handoffDirty} saving={saveHandoff.isPending} invalid={handoffInvalid}
        onSave={() => saveHandoff.mutate(draft)} onDiscard={() => reset(['handoffEnabled', 'handoff'])}
        toggle={{ on: draft.handoffEnabled, onToggle: (v) => patch({ handoffEnabled: v }) }}>
        {/* v12.4.9 (#459): .ho-disabled now greys only - it no longer sets pointer-events:none on this
            whole subtree. The Whisper and "all agents busy" editors and their token pickers stay
            clickable in the greyed state, which is what an agent needs to write them before turning
            handoff on; everything the greying is meant to disable now carries its own `disabled`. */}
        <div className={`ai-agent-body${draft.handoffEnabled ? '' : ' ho-disabled'}`}>
          <div className="aa-subhead">Handoff channels</div>
          <div className="ho-cards">
            <div className={`ho-card${draft.handoff.liveOn ? ' on' : ''}`}>
              <div className="ho-card-head">
                <span className="ho-card-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg></span>
                <div className="ho-card-tt"><b>Live phone line</b><span>Dial an external number via PSTN.</span></div>
                <button type="button" className={`day-toggle${draft.handoff.liveOn ? '' : ' off'}`} onClick={() => toggleCard('live')} disabled={!draft.handoffEnabled} aria-label="Toggle live phone line"><span className="knob" /></button>
              </div>
              <div className="ho-card-body">
                {/* v12.10.5 (UI-LOCK-12.10.5 v1.3): number + pill, keys, Verify. */}
                <HandoffLineField label="Business-hours line" number={draft.handoff.bizLine}
                  keys={draft.handoff.bizKeys} check={draft.handoff.checks?.biz}
                  disabled={!draft.handoff.liveOn} verifying={verifyLine.isPending && verifyLine.variables === 'biz'}
                  onNumber={(v) => patchHandoff({ bizLine: v })} onKeys={(v) => patchHandoff({ bizKeys: v })} onVerify={() => startVerify('biz')} onCancel={() => cancelVerify.mutate('biz')} />
                {verifyErr.biz && <div className="hl-err">{verifyErr.biz}</div>}
              </div>
            </div>
            <div className="ho-card">
              <div className="ho-card-head">
                <span className="ho-card-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg></span>
                <div className="ho-card-tt"><b>Logged-in platform user</b><span>Ring your users in-app (WebRTC). {LATER_BUILD}.</span></div>
                {/* v12.10.11.1 (#1482): not built yet - locked off (the server also stores it off) */}
                <button type="button" className="day-toggle off" disabled title={LATER_BUILD} aria-label="Toggle platform user"><span className="knob" /></button>
              </div>
              <div className="ho-card-body">
                <div className="aa-f">
                  <label>Users to ring <span className="aa-hint">— {draft.handoff.users.length} selected</span></label>
                  <select className="user-add" value="" onChange={(e) => { if (e.target.value) toggleHandoffUser(Number(e.target.value)); }} disabled title={LATER_BUILD}>
                    <option value="">Add a user…</option>
                    {draft.platformUsers.filter((u) => !draft.handoff.users.includes(u.id)).map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
                  </select>
                  {draft.handoff.users.length > 0 ? (
                    <div className="user-pills">
                      {draft.handoff.users.map((id) => {
                        const u = draft.platformUsers.find((x) => x.id === id);
                        if (!u) return null;
                        return (
                          <span key={id} className="user-pill"><span className="up-av">{u.name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}</span>{u.name}<button type="button" onClick={() => toggleHandoffUser(id)} disabled={!draft.handoff.userOn} aria-label={`Remove ${u.name}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg></button></span>
                        );
                      })}
                    </div>
                  ) : <div className="user-empty">No users selected yet.</div>}
                </div>
              </div>
            </div>
          </div>
          {!(draft.handoff.liveOn || draft.handoff.userOn) && (
            <div className="ho-warn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg> Enable at least one channel, or handoff will go straight to the fallback.</div>
          )}

          <div className="aa-subhead">Preferences</div>
          <div className="aa-fr"><div className="aa-f full">
            <label>Handoff preference
              <HelpTooltip sections={[
                ['What it does', 'Which channel the AI tries first when handing a live caller to a human — the phone line or a logged-in platform user.'],
                ['When it fires', 'The moment a handoff is triggered, when both channels are enabled. The unselected channel becomes the backup.'],
                ['Example', <>Set to <em>Logged-in user</em> → the AI rings platform users first; if none answer within the ring timeout, it dials the phone line.</>],
              ]} />
              <span className="aa-hint">{draft.handoff.liveOn && draft.handoff.userOn ? '— tried first; the other is the backup' : '— set automatically (one channel enabled)'}</span>
            </label>
            <div className="seg-group">
              <button type="button" className={`seg${draft.handoff.preference === 'live' ? ' on' : ''}`} onClick={() => patchHandoff({ preference: 'live' })} disabled title={LATER_BUILD}><span className="seg-dot" />Phone line</button>
              <button type="button" className={`seg${draft.handoff.preference === 'user' ? ' on' : ''}`} onClick={() => patchHandoff({ preference: 'user' })} disabled title={LATER_BUILD}><span className="seg-dot" />Logged-in user</button>
            </div>
          </div></div>

          {/* v12.10.1 (#1086): Whisper to agent moved to the Transfer to staff / Route to operator cards. */}
          <div className="aa-fr">
            <div className="aa-f">
              <label>Ring strategy
                <HelpTooltip sections={[
                  ['What it does', 'How multiple logged-in platform users are rung when a handoff goes to them.'],
                  ['When it fires', 'On every handoff to platform users, when more than one user is selected.'],
                  ['Example', <><em>Ring all together</em> = everyone’s device rings at once. <em>Ring in order</em> = one at a time. <em>Longest idle first</em> = whoever has been free longest.</>],
                ]} />
                <span className="aa-hint">{draft.handoff.userOn ? '— for platform users' : '— n/a (no platform users)'}</span>
              </label>
              <select value={draft.handoff.ringStrategy} onChange={(e) => patchHandoff({ ringStrategy: e.target.value })} disabled title={LATER_BUILD}>
                <option value="all">Ring all together</option>
                <option value="sequential">Ring in order</option>
                <option value="longest_idle">Longest idle first</option>
              </select>
            </div>
            <div className="aa-f">
              <label>Ring timeout
                <HelpTooltip sections={[
                  ['What it does', 'How long the AI rings the preferred channel before trying the backup (or the fallback).'],
                  ['When it fires', 'Counted from the moment ringing starts on each handoff attempt.'],
                  ['Example', <>Set to <em>20 sec</em> → if no one answers within 20 seconds, the AI moves on to the next channel.</>],
                ]} />
                <span className="aa-hint">— before moving on</span>
              </label>
              {/* v12.4.9 (#459): explicit disabled - it used to be held back only by the container's
                  pointer-events, which also killed the editors below that must stay clickable. */}
              <div className="inp-suffix"><input type="number" value={draft.handoff.ringTimeoutSec} onChange={(e) => patchHandoff({ ringTimeoutSec: Number(e.target.value) || 0 })} disabled={!draft.handoffEnabled} /><span>sec</span></div>
            </div>
          </div>

          {/* v12.10.1 (#1086): "If no one is available" (Fallback action, "all agents busy") moved to the transfer cards. */}
          {/* v12.10.2 (#1114): "After hours" - the After-hours line OR the Outside operator line, exactly one switched on. His
              words 10/2: "only one can be selected either operator or after-hours. both can't be selected." Office hours always
              reach the business-hours line; after hours reach only the line switched on here; a workflow card can send a call
              only to a line that is switched on. */}
          <div className="aa-subhead">After hours
            <HelpTooltip sections={[
              ['What it does', 'Who a caller reaches outside office hours: the After-hours line OR the Outside operator line - only one can be switched on.'],
              ['When it fires', 'When a caller asks for a person after hours, or a card sends the call there. Neither switched on: the caller goes to voicemail.'],
              ['Example', 'Outside operator line on - after hours every "can I talk to someone" goes to your answering service.'],
            ]} />
          </div>
          <div className="ho-cards">
            <div className={`ho-card${draft.handoff.afterHoursOn ? ' on' : ''}`}>
              <div className="ho-card-head">
                <span className="ho-card-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg></span>
                <div className="ho-card-tt"><b>After-hours line</b><span>Dial your own after-hours number.</span></div>
                <button type="button" className={`day-toggle${draft.handoff.afterHoursOn ? '' : ' off'}`}
                  onClick={() => patchHandoff(draft.handoff.afterHoursOn ? { afterHoursOn: false } : { afterHoursOn: true, operatorOn: false })}
                  disabled={!draft.handoffEnabled || (!draft.handoff.afterHoursOn && !!ahWhy)} title={!draft.handoff.afterHoursOn && ahWhy ? ahWhy : undefined}
                  aria-label="Toggle after-hours line"><span className="knob" /></button>
              </div>
              {/* v12.10.5 (#1128): a held switch says why. */}
              {!draft.handoff.afterHoursOn && ahWhy && <div className="hl-why">{ahWhy}</div>}
              <div className="ho-card-body">
                <HandoffLineField label="After-hours line" number={draft.handoff.afterHoursLine}
                  keys={draft.handoff.afterHoursKeys} check={draft.handoff.checks?.afterHours}
                  disabled={!draft.handoffEnabled} verifying={verifyLine.isPending && verifyLine.variables === 'afterHours'}
                  onNumber={(v) => patchHandoff({ afterHoursLine: v, ...(toE164(v) ? {} : { afterHoursOn: false }) })}
                  onKeys={(v) => patchHandoff({ afterHoursKeys: v })} onVerify={() => startVerify('afterHours')} onCancel={() => cancelVerify.mutate('afterHours')} />
                {verifyErr.afterHours && <div className="hl-err">{verifyErr.afterHours}</div>}
              </div>
            </div>
            <div className={`ho-card${draft.handoff.operatorOn ? ' on' : ''}`}>
              <div className="ho-card-head">
                <span className="ho-card-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg></span>
                <div className="ho-card-tt"><b>Outside operator line</b><span>Dial an answering service or outside operator.</span></div>
                <button type="button" className={`day-toggle${draft.handoff.operatorOn ? '' : ' off'}`}
                  onClick={() => patchHandoff(draft.handoff.operatorOn ? { operatorOn: false } : { operatorOn: true, afterHoursOn: false })}
                  disabled={!draft.handoffEnabled || (!draft.handoff.operatorOn && !!opWhy)} title={!draft.handoff.operatorOn && opWhy ? opWhy : undefined}
                  aria-label="Toggle outside operator line"><span className="knob" /></button>
              </div>
              {!draft.handoff.operatorOn && opWhy && <div className="hl-why">{opWhy}</div>}
              <div className="ho-card-body">
                <div className="hl-note">
                  <HelpTooltip sections={[
                    ['What it does', 'An answering service or outside operator. Switched on, it is the after-hours destination for callers who ask for a person.'],
                    ['When it fires', 'After hours when it is switched on - and at ANY hour when a card sends the call to Route to operator, once this line is Verified (the switch does not matter for that).'],
                    ['Example', '+1 (212) 555-0100 - your answering service.'],
                  ]} /> Route to operator dials this line at any hour once it is Verified.
                </div>
                <HandoffLineField label="Operator line" number={draft.handoff.operatorLine}
                  keys={draft.handoff.operatorKeys} check={draft.handoff.checks?.operator}
                  disabled={!draft.handoffEnabled} verifying={verifyLine.isPending && verifyLine.variables === 'operator'}
                  onNumber={(v) => patchHandoff({ operatorLine: v, ...(toE164(v) ? {} : { operatorOn: false }) })}
                  onKeys={(v) => patchHandoff({ operatorKeys: v })} onVerify={() => startVerify('operator')} onCancel={() => cancelVerify.mutate('operator')} />
                {verifyErr.operator && <div className="hl-err">{verifyErr.operator}</div>}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {toast && (
        <div className="settings-toast-wrap" role="status" aria-live="polite">
          <div className="settings-toast">{toast}</div>
        </div>
      )}
    </div>
  );
}

function Section(props: {
  title: string; description: string; dirty: boolean; saving: boolean;
  onSave: () => void; onDiscard: () => void; children: ReactNode;
  toggle?: { on: boolean; onToggle: (v: boolean) => void };
  headerAction?: ReactNode;   // AAR6: an extra control rendered in the section header (e.g. "Add identifier")
  invalid?: string;           // v12.10.5 (#1122): Save stays off while a field is invalid; this says why
}) {
  return (
    // AAR12: reflect dirty state on the card itself (amber highlight) like the org sections.
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        {/* AAR14: title + actions on one row, caption as a full-width line below — matches the
            Organization sections + the prototype (.sec-head: .sh-top then .sec-desc). */}
        <div className="settings-section-headrow">
          <h2>{props.title}</h2>
          <div className="settings-actions">
            {props.toggle && (
              // AAR1: real toggle switch (day-toggle) instead of a raw checkbox.
              <label className="aa-sec-enable"><span>Enabled</span>
                <button type="button" className={`day-toggle${props.toggle.on ? '' : ' off'}`} onClick={() => props.toggle!.onToggle(!props.toggle!.on)} aria-label="Enabled" aria-pressed={props.toggle.on}><span className="knob" /></button>
              </label>
            )}
            {props.headerAction}
            <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
            <button type="button" className="settings-primary" disabled={!props.dirty || props.saving || !!props.invalid} title={props.invalid || undefined} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
          </div>
        </div>
        <p className="aa-sec-desc">{props.description}</p>
      </header>
      <div className="aa-section-body">{props.children}</div>
    </section>
  );
}

// Agent Details helpers.
function langName(code: string): string {
  if (!code) return '';
  try {
    const loc = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
    return new Intl.DisplayNames([loc], { type: 'language' }).of(code.toLowerCase()) || code;
  } catch { return code; }
}
function voicesFor(catalog: AiVoiceOption[], code: string): AiVoiceOption[] {
  const c = (code || '').toLowerCase();
  return catalog.filter((v) => (v.language || '').toLowerCase() === c);
}
function resolveVoiceCode(stored: string, opts: AiVoiceOption[]): string {
  if (opts.some((o) => o.code === stored)) return stored;
  const byVoice = opts.find((o) => o.voiceIdOrName === stored);
  return byVoice ? byVoice.code : stored;
}
// AAR3: per-language greeting sample (ported from the prototype), so the table shows each language
// in its own script rather than the English sample on every row.
const GREETINGS: Record<string, (n: string) => string> = {
  EN: (n) => `Hi, I am ${n}. How can I help you today?`,
  ES: (n) => `Hola, soy ${n}. ¿Cómo puedo ayudarle hoy?`,
  ZH: (n) => `您好，我是${n}。今天我能为您做些什么？`,
  RU: (n) => `Здравствуйте, меня зовут ${n}. Чем я могу вам помочь?`,
  KO: (n) => `안녕하세요, 저는 ${n}입니다. 무엇을 도와드릴까요?`,
  HI: (n) => `नमस्ते, मैं ${n} हूँ। मैं आपकी कैसे मदद कर सकती हूँ?`,
  VI: (n) => `Xin chào, tôi là ${n}. Tôi có thể giúp gì cho bạn hôm nay?`,
  TL: (n) => `Kumusta, ako si ${n}. Paano kita matutulungan ngayon?`,
  AR: (n) => `مرحباً، أنا ${n}. كيف يمكنني مساعدتك اليوم؟`,
  FR: (n) => `Bonjour, je suis ${n}. Comment puis-je vous aider aujourd'hui ?`,
  DE: (n) => `Hallo, ich bin ${n}. Wie kann ich Ihnen heute helfen?`,
  PT: (n) => `Olá, eu sou ${n}. Como posso ajudá-lo hoje?`,
  JA: (n) => `こんにちは、${n}です。本日はどのようなご用件でしょうか？`,
  GU: (n) => `નમસ્તે, હું ${n} છું. હું તમારી કેવી રીતે મદદ કરી શકું?`,
  IT: (n) => `Salve, sono ${n}. Come posso aiutarla oggi?`,
  PL: (n) => `Dzień dobry, nazywam się ${n}. W czym mogę pomóc?`,
};
function sampleGreeting(code: string, agentName: string): string {
  const name = (agentName || '').trim() || 'your assistant';
  const fn = GREETINGS[(code || '').toUpperCase()] || GREETINGS.EN;
  return fn(name);
}

// v12.9.27 (#1044): LOCALES / localeFor removed with the Locale column - a fixed display list, not from the voice agent.
function codeBadge(code: string): string { return (code || '').toUpperCase(); }

// AAR9: prototype "helpful default" copy, shown as placeholders (greyed, not persisted) when a
// per-language message is empty — so the editor is never a blank box.
const VM_DEFAULTS: Record<string, string> = {
  EN: `You've reached the voicemail for {organization.name}. Please leave a message after the tone with your name, callback number, and reason for calling.`,
  ES: `Ha llamado al buzón de voz de {organization.name}. Por favor, deje un mensaje después del tono con su nombre, número de teléfono y el motivo de su llamada. Gracias.`,   // v12.10.11.1 (#1465): proofread (Santos + Raquel)
  ZH: `您已接通{organization.name}的语音信箱。请在提示音后留言，并留下您的姓名、回电号码和来电事由。`,
  RU: `Вы позвонили на голосовую почту {organization.name}. Пожалуйста, оставьте сообщение после сигнала, указав ваше имя, номер для обратного звонка и причину обращения.`,
  KO: `{organization.name} 음성 사서함에 연결되었습니다. 신호음이 울린 후 성함, 회신 전화번호, 통화 사유를 남겨 주세요.`,
  HI: `आपने {organization.name} के वॉइसमेल से संपर्क किया है। कृपया टोन के बाद अपना नाम, कॉलबैक नंबर और कॉल करने का कारण बताते हुए संदेश छोड़ें।`,
};
const BUSY_DEFAULTS: Record<string, string> = {
  EN: `I'm sorry, no representative is available to take your call right now. How would you like to proceed?`,
  ES: `Lo siento, no hay ningún representante disponible para atender su llamada en este momento. ¿Cómo desea continuar?`,
  ZH: `抱歉，目前没有可以接听您电话的代表。请问您想如何继续？`,
  RU: `Извините, сейчас нет свободных представителей, чтобы принять ваш звонок. Как вы хотели бы продолжить?`,
  KO: `죄송합니다. 지금은 전화를 받을 수 있는 담당자가 없습니다. 어떻게 진행하시겠습니까?`,
  HI: `क्षमा करें, इस समय आपकी कॉल लेने के लिए कोई प्रतिनिधि उपलब्ध नहीं है। आप कैसे आगे बढ़ना चाहेंगे?`,
};
// v12.10.1 (#1086): the tooltip on every control that is shown but not built yet (HANDOFF-TRANSFER-DESIGN v1.0 §5).
const LATER_BUILD = 'Available in a later build';
const WHISPER_DEFAULT = 'Incoming caller {caller.name}, calling about {slot.reason}.';
function vmDefault(code: string): string { return VM_DEFAULTS[(code || '').toUpperCase()] || VM_DEFAULTS.EN; }
function busyDefault(code: string): string { return BUSY_DEFAULTS[(code || '').toUpperCase()] || BUSY_DEFAULTS.EN; }

// AAR2: render an E.164 US/Canada number as +1 (640) 600-8843. Non-NANP numbers pass through.
function formatPhone(raw: string | null): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return trimmed;
}

// Shared per-language message editor: language tabs + the token textarea (TokenPicker).
function LanguageMessageEditor({ langs, messages, onChange, disabled, onPreview, placeholderFor, tokenCtx, groups, known }: {
  langs: { code: string }[];
  messages: AiLangMessage[];
  onChange: (code: string, value: string) => void;
  disabled?: boolean;
  onPreview?: (code: string, text: string) => void;     // AAR5: Preview the current language's message
  placeholderFor?: (code: string) => string;            // AAR9: greyed default copy when empty
  tokenCtx?: TokenContext;                               // v12.2.20.0: resolve {organization.*} in the preview
  groups?: TokenGroup[];                                 // v12.7.3 (#649): the canonical catalog, not the static default
  known?: string[];                                      // v12.9.22 (#886): unresolved-token warning set
}) {
  const codes = langs.map((l) => l.code);
  const [active, setActive] = useState(codes[0] ?? '');
  if (codes.length === 0) return <p className="aa-sub-note">Add a language in Agent Details to edit per-language messages.</p>;
  const cur = codes.includes(active) ? active : codes[0];
  // AAR9: when a language has no stored message yet (null), pre-fill the editor with the default copy
  // as REAL editable text (not a placeholder) so the user can tweak instead of retyping. Once they
  // edit (even to ''), the stored string is used as-is. Unedited defaults stay non-dirty (not saved).
  const entry = messages.find((m) => m.code === cur);
  const stored = entry ? entry.message : null;
  const display = stored != null ? stored : (placeholderFor?.(cur) ?? '');
  // v12.9.22 (#886): the tenant's FIRST language is the auto-translate source, matching
  // KnowledgeBaseSections. Same-language is a no-op - PromptEditor hides the control itself.
  const srcLang = codes[0] ?? 'en';
  const srcStored = messages.find((m) => m.code === srcLang)?.message;
  const srcText = srcStored != null ? srcStored : (placeholderFor?.(srcLang) ?? '');
  // v12.2.20.0 (PRE-TOK2): live token-resolved preview — the text as the caller will actually hear it.
  const preview = resolveTokens(display, tokenCtx ?? {});
  return (
    <div className="ah-msg">
      <div className="lt-bar"><div className="lt-tabs">
        {codes.map((c) => (
          <button key={c} type="button" className={`lt-tab${c === cur ? ' on' : ''}`} onClick={() => setActive(c)}>{langName(c)} &middot; {codeBadge(c)}</button>
        ))}
      </div></div>
      {/* ══ v12.9.22 (#886): THE SHARED PROMPT EDITOR, NOT A SECOND ONE. ═══════════════════════════════
          His instruction, 2026-09-28: *"will you be also able to replace this prompt editor it is throught
          AI agent - some old gray box without support for auto translate"*.

          THE MISSING AUTO-TRANSLATE WAS NOT AN OVERSIGHT - IT WAS THE COST OF A SECOND CONTROL. This block
          rendered its own TokenPicker and its own PREVIEW box, so it never had #238-F's on-demand translate
          (PromptEditor.tsx), nor the machine-filled badge that marks text no person has read yet. Meanwhile
          KnowledgeBaseSections says in as many words that it uses "the same control ... every workflow card
          uses". The AI Agent page was the one that went its own way. #836, fifteenth occurrence.

          `translateSource` is the FIRST language's text - the tenant's default - which is the same source
          KnowledgeBaseSections picks. PromptEditor only offers the control when that text exists and the
          language differs, and it never overwrites what someone typed.

          `disabled` survives as a fieldset wrapper because PromptEditor takes no such prop; a disabled
          fieldset disables every control inside it, which is what the Voicemail toggle needs. */}
      <fieldset className="aa-msg-fs" disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <PromptEditor
          value={display}
          onChange={(v) => onChange(cur, v)}
          language={cur}
          groups={groups}
          known={known}
          platformDefault={placeholderFor?.(cur)}   /* v12.10.10 (#1408): the platform message is written text, not grey */
          translateSource={srcText}
          translateSourceLang={srcLang}
        />
      </fieldset>
    </div>
  );
}

// Phone status card — 4 states (connected / provisioned / down / not_provisioned) + capability pills.
// Exported so the Workflow "Inbound call" pre-routing card can reuse the SAME phone element/codebase
// (see components/aiagent/PhoneActivationSection.tsx) rather than reimplementing it.
export function PhoneCard({ status, number, facilities }: { status: string; number: string | null; facilities: { name: string; ok: boolean }[] }) {
  const meta: Record<string, { cls: string; sub: string }> = {
    connected: { cls: 'cn', sub: 'Your number is live and ready.' },
    provisioned: { cls: 'pv', sub: 'Number assigned. Finishing connection…' },
    down: { cls: 'dn', sub: 'Phone unreachable — calls aren’t connecting. Contact your administrator.' },
    not_provisioned: { cls: 'np', sub: 'No number is assigned yet. Provisioning is managed by your administrator.' },
  };
  const st = meta[status] ? status : 'not_provisioned';
  const m = meta[st];
  const isNP = st === 'not_provisioned';

  let graphic: ReactNode;
  if (st === 'connected') {
    graphic = (
      <span className="phone-live">
        <span className="wave w1" /><span className="wave w2" />
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
        <span className="live-dot" />
      </span>
    );
  } else if (st === 'provisioned') {
    graphic = (<span className="phone-badge pv"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></span>);
  } else if (st === 'down') {
    graphic = (<span className="phone-badge dn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34A19.79 19.79 0 0 1 3.07 8.63" /><path d="M23 1 1 23" /></svg></span>);
  } else {
    graphic = (<span className="phone-badge np"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg></span>);
  }

  return (
    <div className={`phone-card ${m.cls}`}>
      <div className="phone-left">
        <div className="phone-gfx">{graphic}</div>
        <div className="phone-main">
          {isNP ? <div className="phone-num muted">— not assigned —</div> : <div className="phone-num">{formatPhone(number)}</div>}
          <div className="phone-subtle">{m.sub}</div>
        </div>
      </div>
      {!isNP && (
        <div className="phone-caps">
          <span className="caps-lbl">Handles</span>
          <div className="caps-pills">
            {facilities.map((f) => f.ok ? (
              <span key={f.name} className="fac ok"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg> {f.name}</span>
            ) : (
              <span key={f.name} className="fac no"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg> {f.name} <span className="fac-na">not supported</span></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
